import { Button, Form, message, Modal } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showConfirmUploadModal } from './components/ConfirmUploadModal';
import {
  StyledCollapse,
  CollapseHeader,
  HeaderLeft,
  HeaderTitle,
  FormContent,
  RequirementsSection,
  RequirementsContent,
  FormFooter
} from './SingleFileUploader.styles';

import { UploadDragAndDrop } from '@/components/UploadDragAndDrop';
import { useUploadConfirmModal } from '@/store/ui-preferences';

interface SingleFileUploaderProps {
  title: string | React.ReactNode;
  fieldName: string;
  requirements: React.ReactNode;
  onSubmit: (fileId?: string) => Promise<void>;
  acceptedTypes?: string[];
  maxSizeInMB?: number;
  initialFileId?: string;
  disabled?: boolean;
  blockAfterSubmit?: boolean;
  titleText?: string;
}

export const SingleFileUploader: React.FC<SingleFileUploaderProps> = ({
  title,
  fieldName,
  requirements,
  onSubmit,
  acceptedTypes,
  maxSizeInMB,
  initialFileId,
  disabled = false,
  blockAfterSubmit = false,
  titleText
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(!!initialFileId);
  const watchedField = Form.useWatch(fieldName, form);
  const { dontShowAgain } = useUploadConfirmModal();

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      await onSubmit(values[fieldName]);
      setHasBeenSubmitted(true);
    } catch (error) {
      message.error(t('DefaultTranslations:ERROR_SENDING_DOCUMENT'));
      setLoading(false);
    } finally {
      setActiveKey([]);
      setLoading(false);
    }
  };

  const handleSubmitClick = () => {
    if (dontShowAgain) {
      handleSubmit();
    } else {
      const filenameForModal = titleText || (typeof title === 'string' ? title : '');
      showConfirmUploadModal({
        modal,
        title: t('DefaultTranslations:CONFIRM_UPLOAD_TITLE'),
        description: t('DefaultTranslations:CONFIRM_UPLOAD_DESCRIPTION', {
          filename: filenameForModal
        }),
        dontShowAgainLabel: t('DefaultTranslations:DONT_SHOW_AGAIN'),
        okText: t('DefaultTranslations:SEND'),
        cancelText: t('DefaultTranslations:CANCEL'),
        onConfirm: handleSubmit
      });
    }
  };

  const shouldShowSubmitButton = !blockAfterSubmit || !hasBeenSubmitted;

  const renderHeader = () => (
    <CollapseHeader>
      <HeaderLeft>
        <HeaderTitle>{title}</HeaderTitle>
      </HeaderLeft>
    </CollapseHeader>
  );

  return (
    <>
      {contextHolder}
      <StyledCollapse
        activeKey={activeKey}
        onChange={setActiveKey}
        expandIconPosition="end"
        items={[
          {
            key: '1',
            label: renderHeader(),
            children: (
              <FormContent>
                <Form
                  form={form}
                  layout="vertical"
                  style={{ width: '100%' }}
                  initialValues={{
                    [fieldName]: initialFileId
                  }}>
                  <Form.Item name={fieldName} noStyle>
                    <UploadDragAndDrop
                      acceptedTypes={acceptedTypes}
                      maxSizeInMB={maxSizeInMB}
                      disabled={disabled}
                      allowEdit={shouldShowSubmitButton}
                    />
                  </Form.Item>
                </Form>

                <RequirementsSection>
                  <RequirementsContent>{requirements}</RequirementsContent>
                </RequirementsSection>

                {shouldShowSubmitButton && (
                  <FormFooter>
                    <Button
                      type="primary"
                      onClick={handleSubmitClick}
                      loading={loading}
                      disabled={disabled || !watchedField}>
                      {loading
                        ? t('DefaultTranslations:SENDING')
                        : t('DefaultTranslations:SEND_DOCUMENT')}
                    </Button>
                  </FormFooter>
                )}
              </FormContent>
            )
          }
        ]}
      />
    </>
  );
};
